<template>
  <v-card class="bank-account-dialog">
    <v-card-title class="justify-center text-h4 primary--text px-8 pt-6 pb-4">
      บัญชีรับชำระของร้าน
    </v-card-title>
    <v-card-text class="popup-background px-4 px-md-8 pt-2 pb-4">
      <v-row justify="center">
        <v-col cols="12">
          <v-alert v-if="channelLoadError" type="error" dense text class="mb-4">
            <div class="d-flex align-center justify-space-between">
              <span>{{ channelLoadError }}</span>
              <v-btn text small color="error" @click="getPaymentChannels">ลองใหม่</v-btn>
            </div>
          </v-alert>
          <v-alert v-if="submitError" type="error" dense text class="mb-4">
            {{ submitError }}
          </v-alert>
          <v-alert v-if="hasValidationErrors" type="error" dense text class="mb-4">
            กรุณาตรวจสอบข้อมูลบัญชีรับชำระที่ทำเครื่องหมายไว้
          </v-alert>
          <v-row no-gutters align="center" class="title-table-banks py-2 text-center d-none d-md-flex">
            <v-col cols="3">ชื่อที่ใช้แสดงตอนรับชำระ</v-col>
            <v-col cols="3">ช่องทางรับชำระ</v-col>
            <v-col cols="2">ชื่อบัญชี</v-col>
            <v-col cols="3">เลขบัญชี / PromptPay</v-col>
            <v-col cols="1"></v-col>
          </v-row>
          <v-row
            v-for="(data, index) in list"
            :key="data._id || index"
            no-gutters
            align="start"
            class="bgc-white account-row mb-3"
          >
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="data.bank_name"
                label="ชื่อที่ใช้แสดงตอนรับชำระ"
                outlined
                dense
                hide-details="auto"
                :error-messages="accountError(index, 'bank_name')"
                @input="clearAccountError(index, 'bank_name')"
              />
            </v-col>
            <v-col cols="12" md="3" class="pa-2 payment-channel-col">
              <v-select
                v-model="data.payment_channel_code"
                :items="paymentChannels"
                :loading="loadingChannels"
                item-value="code"
                item-text="name"
                label="ช่องทางรับชำระ"
                outlined
                dense
                hide-details="auto"
                :error-messages="accountError(index, 'payment_channel_code')"
                @change="onChannelChange(data, index)"
              />
            </v-col>
            <v-col cols="12" md="2" class="pa-2 account-name-col">
              <v-text-field
                v-model="data.account_name"
                label="ชื่อบัญชี"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="data.account_number"
                inputmode="numeric"
                pattern="[0-9]*"
                :maxlength="accountNumberMaxLength(data)"
                outlined
                dense
                :label="accountNumberLabel(data)"
                hide-details="auto"
                :error-messages="accountError(index, 'account_number')"
                @keydown.native="allowOnlyAccountNumberKeys($event, index)"
                @paste.native="pasteAccountNumber($event, data, index)"
                @input="onAccountNumberInput(data, index, $event)"
              />
            </v-col>
            <v-col cols="12" md="1" class="text-center pt-2 pt-md-5">
              <v-btn icon small color="error" aria-label="ลบบัญชีรับชำระ" @click="remove(data, index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row no-gutters align="center" v-if="list.length === 0" class="text-center py-3 bgc-white">
            <v-col cols="12">กรุณากดปุ่มเพิ่มบัญชีรับชำระ</v-col>
          </v-row>
          <v-row class="mt-1">
            <v-col cols="12" class="text-left pt-0">
              <v-btn outlined color="primary" small @click="add">
                <v-icon left>mdi-plus</v-icon> เพิ่มบัญชีรับชำระ
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="px-8 pt-2 pb-6">
      <v-spacer />
      <v-btn outlined class="px-5 mr-3" color="grey" :disabled="isSaving" @click="close">ปิด</v-btn>
      <v-btn
        class="px-8"
        color="primary"
        @click="save"
        :loading="isSaving"
        :disabled="isSaving || loadingChannels || !!channelLoadError"
      >ยืนยัน</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  props: {
    item: {
      type: Array,
      default: null,
    },
    delete_bank_account: {
      type: Array,
      default: null,
    },
    saveWithParent: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    submitError: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loadingChannels: false,
      channelLoadError: '',
      paymentChannels: [],
      list: [],
      deleteList: [],
      errors: {},
    };
  },
  created() {
    this.getData();
    this.getPaymentChannels();
  },
  computed: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0;
    },
    isSaving() {
      return this.saving;
    },
  },
  methods: {
    async getPaymentChannels() {
      this.loadingChannels = true;
      this.channelLoadError = '';
      try {
        const response = await this.$axios.post(
          `${this.$baseUrl}/branch/get_payment_channel_code`,
          {
            token: this.$jwt.sign({}, this.$privateKey, { noTimestamp: true }),
          }
        );
        this.paymentChannels = response.result || [];
      } catch (err) {
        this.channelLoadError = 'ไม่สามารถโหลดรายการช่องทางรับชำระได้';
      } finally {
        this.loadingChannels = false;
      }
    },
    save() {
      if (!this.validateAccounts()) return;
      const bankAccounts = this.list.map((account) => ({ ...account }));
      const deleteList = [...this.deleteList];

      if (this.saveWithParent) {
        this.$emit('onSubmit', bankAccounts, deleteList);
        return;
      }

      this.$emit('onClose');
      this.$emit('onChange', bankAccounts, deleteList);
    },
    close() {
      this.$emit('onClose');
    },
    remove(account, index) {
      this.deleteList.push(account._id || account.account_number || '');
      this.list.splice(index, 1);
      this.errors = {};
    },
    add() {
      this.list.push({
        bank_name: '',
        payment_channel_code: '',
        account_name: '',
        account_number: '',
      });
    },
    getData() {
      this.list = (this.item || []).map((account) => ({ ...account }));
      this.deleteList = [...(this.delete_bank_account || [])];
    },
    accountNumberLabel(account) {
      return account.payment_channel_code === 'PROMPTPAY'
        ? 'หมายเลข PromptPay'
        : 'เลขบัญชี / หมายเลขบัญชี';
    },
    accountNumberMaxLength(account) {
      return account.payment_channel_code === 'PROMPTPAY' ? 13 : undefined;
    },
    accountError(index, field) {
      return this.errors[index] && this.errors[index][field]
        ? this.errors[index][field]
        : '';
    },
    clearAccountError(index, field) {
      const accountErrors = this.errors[index];
      if (!accountErrors || !accountErrors[field]) return;

      this.$delete(accountErrors, field);
      if (Object.keys(accountErrors).length === 0) this.$delete(this.errors, index);
    },
    setAccountError(index, field, message) {
      if (!this.errors[index]) this.$set(this.errors, index, {});
      this.$set(this.errors[index], field, message);
    },
    onChannelChange(account, index) {
      account.payment_channel_code = String(account.payment_channel_code || '').trim().toUpperCase();
      this.clearAccountError(index, 'payment_channel_code');
      this.clearAccountError(index, 'account_number');
    },
    allowOnlyAccountNumberKeys(event, index) {
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
        'ArrowLeft', 'ArrowRight', 'Home', 'End',
      ];
      const modifierKeys = ['Control', 'Meta'];
      const isShortcut = (event.ctrlKey || event.metaKey)
        && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
      if (
        allowedKeys.includes(event.key)
        || modifierKeys.includes(event.key)
        || isShortcut
        || /^\d$/.test(event.key)
      ) return;

      event.preventDefault();
      this.setAccountError(
        index,
        'account_number',
        'เลขบัญชีกรอกได้เฉพาะตัวเลข'
      );
    },
    pasteAccountNumber(event, account, index) {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');
      const numericText = pastedText.replace(/\D/g, '');
      const input = event.target;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || start;
      const currentValue = String(account.account_number || '');
      account.account_number = currentValue.slice(0, start)
        + numericText + currentValue.slice(end);

      this.onAccountNumberInput(account, index, account.account_number);
      if (pastedText !== numericText) {
        this.setAccountError(index, 'account_number', 'เลขบัญชีกรอกได้เฉพาะตัวเลข');
      }
    },
    onAccountNumberInput(account, index, value) {
      const channelCode = String(account.payment_channel_code || '').trim().toUpperCase();
      const accountNumber = String(value === undefined ? account.account_number : value || '');
      const numericAccountNumber = accountNumber.replace(/\D/g, '');
      const normalizedAccountNumber = channelCode === 'PROMPTPAY'
        ? numericAccountNumber.slice(0, 13)
        : numericAccountNumber;
      account.account_number = normalizedAccountNumber;
      if (accountNumber !== numericAccountNumber) {
        this.setAccountError(index, 'account_number', 'เลขบัญชีกรอกได้เฉพาะตัวเลข');
        return;
      }

      const accountErrors = this.errors[index];
      if (channelCode !== 'PROMPTPAY' || !accountErrors || !accountErrors.account_number) {
        this.clearAccountError(index, 'account_number');
        return;
      }

      if (/^(0\d{9}|\d{13})$/.test(account.account_number)) {
        this.clearAccountError(index, 'account_number');
      } else {
        this.setAccountError(index, 'account_number', 'หมายเลข PromptPay ต้องเป็นเบอร์มือถือ 10 หลักที่ขึ้นต้น 0 หรือเลขบัตร 13 หลัก');
      }
    },
    isNewAccount(account) {
      return !account._id;
    },
    normalizePromptPayNumber(account) {
      account.account_number = String(account.account_number || '').replace(/[\s-]/g, '');
    },
    validateAccounts() {
      const errors = {};
      this.list.forEach((account, index) => {
        const channelCode = String(account.payment_channel_code || '').trim().toUpperCase();
        const needsNewSchemaValidation = this.isNewAccount(account) || Boolean(channelCode);
        const accountErrors = {};

        if (!needsNewSchemaValidation) return;
        if (!String(account.bank_name || '').trim()) {
          accountErrors.bank_name = 'กรุณาระบุชื่อที่ใช้แสดงตอนรับชำระ';
        }
        if (!channelCode) {
          accountErrors.payment_channel_code = 'กรุณาเลือกช่องทางรับชำระ';
        } else if (!this.paymentChannels.some((channel) => channel.code === channelCode)) {
          accountErrors.payment_channel_code = 'ช่องทางรับชำระไม่ถูกต้อง';
        }

        account.payment_channel_code = channelCode;
        if (channelCode === 'PROMPTPAY') {
          this.normalizePromptPayNumber(account);
          if (!/^(0\d{9}|\d{13})$/.test(account.account_number)) {
            accountErrors.account_number = 'กรุณาระบุเบอร์มือถือ 10 หลักที่ขึ้นต้น 0 หรือเลขบัตร 13 หลัก';
          }
        }
        if (Object.keys(accountErrors).length) errors[index] = accountErrors;
      });
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.title-table-banks {
  background-color: #F5F6FA;
}

@media (min-width: 960px) {
  .payment-channel-col {
    flex: 0 0 20%;
    max-width: 20%;
  }

  .account-name-col {
    flex: 0 0 21.666667%;
    max-width: 21.666667%;
  }
}
</style>
